import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { User } from '@/types'
import { authService, RegisterData } from '@/services/authService'
import { profileService } from '@/services/profileService'
import { useConversationsStore } from './conversationsStore'

interface AuthState {
  user: User | null
  isLoading: boolean
  error: string | null
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  clearError: () => void
  checkAuth: () => Promise<void>
  updateProfile: (data: Partial<User>) => Promise<void>
}

// Deduplication: only one checkAuth in flight at a time
let _checkAuthPromise: Promise<void> | null = null

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const { user } = await authService.login({ email, password })
          set({ user, isLoading: false })
        } catch (error: any) {
          console.error('Erro no login:', error)
          
          // Tratar diferentes tipos de erro
          let errorMessage = 'Erro ao fazer login'
          
          // Prioridade para erros específicos do backend
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.response?.data?.details) {
            if (Array.isArray(error.response.data.details)) {
              errorMessage = error.response.data.details.join(', ')
            } else {
              errorMessage = error.response.data.details
            }
          } else if (error.response?.status === 409) {
            errorMessage = 'Email já está em uso'
          } else if (error.response?.status === 400) {
            errorMessage = 'Dados inválidos'
          } else if (error.message) {
            errorMessage = error.message
          } else if (error.code === 'ERR_NETWORK') {
            errorMessage = 'Erro de conexão com o servidor. Tente novamente.'
          } else if (error.code === 'ECONNREFUSED') {
            errorMessage = 'Servidor offline. Tente novamente mais tarde.'
          }
          
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          throw error
        }
      },

      register: async (data: RegisterData) => {
        set({ isLoading: true, error: null })

        try {
          const { user } = await authService.register(data)
          set({ user, isLoading: false })
        } catch (error: any) {
          console.error('Erro no registro:', error)
          
          // Tratar diferentes tipos de erro
          let errorMessage = 'Erro ao criar conta'
          
          // Prioridade para erros específicos do backend
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message
          } else if (error.response?.data?.details) {
            if (Array.isArray(error.response.data.details)) {
              errorMessage = error.response.data.details.join(', ')
            } else {
              errorMessage = error.response.data.details
            }
          } else if (error.response?.status === 409) {
            errorMessage = 'Email já está em uso'
          } else if (error.response?.status === 400) {
            errorMessage = 'Dados inválidos'
          } else if (error.message) {
            errorMessage = error.message
          } else if (error.code === 'ERR_NETWORK') {
            errorMessage = 'Erro de conexão com o servidor. Tente novamente.'
          } else if (error.code === 'ECONNREFUSED') {
            errorMessage = 'Servidor offline. Tente novamente mais tarde.'
          }
          
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          throw error
        }
      },

      logout: () => {
        authService.logout()
        // Limpar progresso de registro do localStorage
        localStorage.removeItem('registerProgress')
        set({ user: null, error: null })
        // Limpar conversas cacheadas para evitar conflito entre usuários
        useConversationsStore.getState().resetConversations()
      },

      clearError: () => set({ error: null }),

      checkAuth: async () => {
        if (_checkAuthPromise) return _checkAuthPromise

        if (!authService.isAuthenticated()) {
          set({ user: null })
          return
        }

        _checkAuthPromise = (async () => {
          try {
            const user = await authService.getCurrentUser()
            set({ user })
          } catch {
            set({ user: null })
          } finally {
            _checkAuthPromise = null
          }
        })()

        return _checkAuthPromise
      },

      updateProfile: async (data: Partial<Pick<User, 'email' | 'name' | 'username' | 'bio' | 'avatarUrl'>>) => {
        set({ isLoading: true, error: null })
        
        try {
          // Chamar API real
          const response = await profileService.updateProfile(data)
          
          // Atualizar usuário localmente com resposta da API
          set(state => ({
            user: state.user ? { ...state.user, ...response.user } : null,
            isLoading: false
          }))
        } catch (error: any) {
          console.error('Erro ao atualizar perfil:', error)
          
          let errorMessage = 'Erro ao atualizar perfil'
          if (error.response?.data?.error) {
            errorMessage = error.response.data.error
          } else if (error.message) {
            errorMessage = error.message
          }
          
          set({ 
            error: errorMessage,
            isLoading: false 
          })
          throw error
        }
      }
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user })
    }
  )
)